// src/pages/FulfillmentPage/components/FulfillmentHeader.tsx
import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
  Stack,
  Button,
  Fade,
  CircularProgress,
  Avatar,
  alpha,
  useTheme,
  Paper,
  Tooltip
} from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InventoryIcon from "@mui/icons-material/Inventory";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import { Fulfillment } from "../types";
import { statusColor } from "../utils/helpers";
import { ShippingInfo } from "./ShippingInfo";
import { FulfillmentLabel } from "../../../api/fulfillmentApi";
import ReplayIcon from "@mui/icons-material/Replay";

interface FulfillmentHeaderProps {
  fulfillment: Fulfillment;
  latestLabel: FulfillmentLabel | null;
  labelHistory: FulfillmentLabel[];
  actionLoading: boolean;
  isLocalDelivery: boolean;
  isShipping: boolean;
  hasLabelRecord: boolean;
  hasLabelUrl: boolean;
  canStart: boolean;
  canMarkReady: boolean;
  canBuyLabel: boolean;
  canComplete: boolean;
  canMarkDelivered: boolean;
  onStart: () => void;
  onMarkReady: () => void;
  onBuyLabel: () => void;
  onRefundLabel: () => void;
  onComplete: () => void;
  onMarkDelivered: () => void;
}

export const FulfillmentHeader: React.FC<FulfillmentHeaderProps> = ({
  fulfillment,
  latestLabel,
  labelHistory,
  actionLoading,
  isLocalDelivery,
  isShipping,
  hasLabelRecord,
  hasLabelUrl,
  canStart,
  canMarkReady,
  canBuyLabel,
  canComplete,
  canMarkDelivered,
  onStart,
  onMarkReady,
  onBuyLabel,
  onRefundLabel,
  onComplete,
  onMarkDelivered,
}) => {
  const theme = useTheme();

  // Get status color for the header accent
  const getStatusAccentColor = () => {
    switch (fulfillment.status.toUpperCase()) {
      case 'DELIVERED': return theme.palette.success.main;
      case 'SHIPPED':
      case 'OUT_FOR_DELIVERY': return theme.palette.info.main;
      case 'READY': return theme.palette.warning.main;
      case 'PACKING': return theme.palette.primary.main;
      case 'CANCELLED': return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  };

  // Get status label with emoji for better visual cues
  const getStatusLabel = () => {
    switch (fulfillment.status.toUpperCase()) {
      case 'DELIVERED': return '✅ Delivered';
      case 'SHIPPED': return '📦 Shipped';
      case 'OUT_FOR_DELIVERY': return '🚚 Out for Delivery';
      case 'READY': return '📋 Ready';
      case 'PACKING': return '📦 Packing';
      case 'NEW': return '🆕 New';
      case 'CANCELLED': return '❌ Cancelled';
      default: return fulfillment.status;
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: 3,
        width: "100%",
        overflow: "hidden",
        border: '1px solid',
        borderColor: theme.palette.divider,
        bgcolor: theme.palette.background.paper,
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: theme.shadows[2]
        }
      }}
    >
      {/* Status Accent Bar */}
      <Box
        sx={{
          height: 4,
          width: '100%',
          bgcolor: getStatusAccentColor(),
          transition: 'background-color 0.3s ease'
        }}
      />

      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: { xs: 3, lg: 2 },
            alignItems: { xs: "stretch", lg: "center" },
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          {/* Left side - Title and info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <Avatar
                sx={{
                  width: 52,
                  height: 52,
                  bgcolor: alpha(getStatusAccentColor(), 0.1),
                  color: getStatusAccentColor(),
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              >
                <ShoppingBagIcon sx={{ fontSize: 28 }} />
              </Avatar>

              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1.5rem", sm: "1.75rem" },
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em',
                    mb: 0.5,
                    background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.text.secondary} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'inline-block'
                  }}
                >
                  Fulfillment #{fulfillment.id}
                </Typography>

                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      px: 1,
                      py: 0.3,
                      borderRadius: 1
                    }}
                  >
                    #{fulfillment.order_number}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>•</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                    {fulfillment.customer_name || "Customer"}
                  </Typography>
                  {fulfillment.customer_email && (
                    <>
                      <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>•</Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {fulfillment.customer_email}
                      </Typography>
                    </>
                  )}
                </Stack>
              </Box>
            </Stack>

            {/* Status Chips */}
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Tooltip title="Fulfillment type">
                <Chip
                  label={String(fulfillment.type).toUpperCase()}
                  icon={<LocalShippingIcon />}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    border: 'none',
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: 'inherit' }
                  }}
                />
              </Tooltip>

              <Tooltip title="Current status">
                <Chip
                  label={getStatusLabel()}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    bgcolor: alpha(getStatusAccentColor(), 0.1),
                    color: getStatusAccentColor(),
                    border: 'none',
                    fontWeight: 700,
                    px: 0.5
                  }}
                />
              </Tooltip>

              {fulfillment.assigned_to && (
                <Tooltip title="Assigned staff member">
                  <Chip
                    label={`Staff #${fulfillment.assigned_to}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      borderColor: theme.palette.divider,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.05)
                      }
                    }}
                  />
                </Tooltip>
              )}
            </Stack>

            <ShippingInfo
              fulfillment={fulfillment}
              latestLabel={latestLabel}
              labelHistory={labelHistory}
              isShipping={isShipping}
              hasLabelRecord={hasLabelRecord}
              hasLabelUrl={hasLabelUrl}
              actionLoading={actionLoading}
              onRefundLabel={onRefundLabel}
            />
          </Box>

          {/* Right side - Action buttons */}
          <Box sx={{
            width: { xs: "100%", lg: "auto" },
            flexShrink: 0,
            borderTop: { xs: `1px solid ${theme.palette.divider}`, lg: 'none' },
            pt: { xs: 2, lg: 0 },
            mt: { xs: 1, lg: 0 }
          }}>
            <Typography
              variant="caption"
              sx={{
                display: { xs: 'block', lg: 'none' },
                mb: 1.5,
                color: theme.palette.text.secondary,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Available Actions
            </Typography>

            <Box sx={{
              display: "flex",
              gap: 1.5,
              flexWrap: "wrap",
              justifyContent: { xs: "flex-start", lg: "flex-end" }
            }}>
              {canStart && (
                <Fade in={canStart}>
                  <Button
                    variant="contained"
                    startIcon={actionLoading ? <CircularProgress size={18} /> : <PlayArrowIcon />}
                    disabled={actionLoading}
                    onClick={onStart}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      textTransform: "none",
                      fontWeight: 600,
                      boxShadow: 'none',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      '&:hover': {
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.2s ease',
                      minWidth: { xs: '100%', sm: '120px' }
                    }}
                    size="medium"
                  >
                    Start
                  </Button>
                </Fade>
              )}

              {canMarkReady && (
                <Fade in={canMarkReady}>
                  <Button
                    variant="outlined"
                    disabled={actionLoading}
                    onClick={onMarkReady}
                    startIcon={<InventoryIcon />}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      textTransform: "none",
                      fontWeight: 600,
                      borderWidth: 2,
                      borderColor: theme.palette.warning.main,
                      color: theme.palette.warning.main,
                      '&:hover': {
                        borderWidth: 2,
                        borderColor: theme.palette.warning.dark,
                        bgcolor: alpha(theme.palette.warning.main, 0.05),
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.2s ease',
                      minWidth: { xs: '100%', sm: '120px' }
                    }}
                    size="medium"
                  >
                    Mark Ready
                  </Button>
                </Fade>
              )}

              {canBuyLabel && (
                <Fade in={canBuyLabel}>
                  <Button
                    variant="outlined"
                    disabled={actionLoading}
                    onClick={onBuyLabel}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      textTransform: "none",
                      fontWeight: 600,
                      borderWidth: 2,
                      borderColor: theme.palette.info.main,
                      color: theme.palette.info.main,
                      '&:hover': {
                        borderWidth: 2,
                        borderColor: theme.palette.info.dark,
                        bgcolor: alpha(theme.palette.info.main, 0.05),
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.2s ease',
                      minWidth: { xs: '100%', sm: '120px' }
                    }}
                    size="medium"
                  >
                    Buy Label
                  </Button>
                </Fade>
              )}

              {canComplete && (
                <Fade in={canComplete}>
                  <Button
                    variant="contained"
                    disabled={actionLoading}
                    onClick={onComplete}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      textTransform: "none",
                      fontWeight: 600,
                      boxShadow: 'none',
                      background: isLocalDelivery
                        ? `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`
                        : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      '&:hover': {
                        boxShadow: `0 4px 12px ${alpha(isLocalDelivery ? theme.palette.warning.main : theme.palette.primary.main, 0.3)}`,
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.2s ease',
                      minWidth: { xs: '100%', sm: '140px' }
                    }}
                    size="medium"
                  >
                    {isLocalDelivery ? "Out for Delivery" : "Mark Shipped"}
                  </Button>
                </Fade>
              )}

              {canMarkDelivered && (
                <Fade in={canMarkDelivered}>
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleIcon />}
                    disabled={actionLoading}
                    onClick={onMarkDelivered}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      textTransform: "none",
                      fontWeight: 600,
                      boxShadow: 'none',
                      background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                      '&:hover': {
                        boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.2s ease',
                      minWidth: { xs: '100%', sm: '120px' }
                    }}
                    size="medium"
                  >
                    Delivered
                  </Button>
                </Fade>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Paper>
  );
};